import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useSupportClock } from '../../hooks/useSupportClock';
import {
  ticketService,
  SupportNotification,
} from '../../services/ticketService';
import { formatSupportTimeAgo as formatTimeAgo } from '../../utils/supportTime';

import { SUPPORT_NOTIFICATION_REFRESH_EVENT } from './supportNotificationEvents';
import { useNotificationPopover } from './useNotificationPopover';

import './SupportNotificationBell.css';

const POLL_INTERVAL_MS = 30000;
const NOTIFICATION_PREVIEW_WORDS = 7;

const BellIcon: React.FC = () => (
  <svg
    className="support-notification-bell__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const getInternalUrl = (url: string): string => {
  if (!url) return '/support';

  try {
    const parsed = new URL(url, window.location.origin);

    if (parsed.origin !== window.location.origin) return '/support';

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url.startsWith('/') ? url : '/support';
  }
};

const truncateWords = (text: string, maxWords: number): string => {
  const words = text.trim().split(/\s+/).filter(Boolean);

  if (words.length <= maxWords) return words.join(' ');

  return `${words.slice(0, maxWords).join(' ')}...`;
};

const getNotificationPreview = (notification: SupportNotification): string => {
  const preview = typeof notification.data?.preview === 'string'
    ? notification.data.preview
    : notification.body;

  return notification.type === 'loop_input_needed'
    ? preview : truncateWords(preview, NOTIFICATION_PREVIEW_WORDS);
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const SupportNotificationBell: React.FC = () => {
  useSupportClock();
  const navigate = useNavigate();
  const popupId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<SupportNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const clearedNotificationIdsRef = useRef<Set<string>>(new Set());
  const close = useCallback(() => setIsOpen(false), []);
  const position = useNotificationPopover(isOpen, buttonRef, popupRef, close);

  const refreshCount = useCallback(() => ticketService.getUnreadNotificationCount()
    .then(setUnreadCount)
    .catch(() => { /* Keep the last known count while connectivity recovers. */ }), []);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await ticketService.listNotifications({
        page: 1,
        per_page: 20,
        unread_only: true,
      });

      setNotifications(data.notifications.filter(
        (notification) => !clearedNotificationIdsRef.current.has(notification.id)
      ));
      await refreshCount();
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();

    const handleRefresh = () => {
      refreshCount();
      if (isOpen) loadNotifications();
    };
    const interval = window.setInterval(handleRefresh, POLL_INTERVAL_MS);

    window.addEventListener(SUPPORT_NOTIFICATION_REFRESH_EVENT, handleRefresh);
    window.addEventListener('focus', handleRefresh);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(SUPPORT_NOTIFICATION_REFRESH_EVENT, handleRefresh);
      window.removeEventListener('focus', handleRefresh);
    };
  }, [isOpen, loadNotifications, refreshCount]);

  const toggleOpen = () => {
    if (!isOpen) { setLoading(true); void loadNotifications(); }
    setIsOpen((open) => !open);
  };

  const handleNotificationClick = async (notification: SupportNotification) => {
    if (!notification.read_at) {
      await ticketService.markNotificationRead(notification.id).catch(() => {});
      setUnreadCount((count) => Math.max(0, count - 1));
    }

    setIsOpen(false);
    navigate(getInternalUrl(notification.url));
  };

  const handleClearAll = async () => {
    notifications.forEach((notification) => {
      clearedNotificationIdsRef.current.add(notification.id);
    });
    await ticketService.markAllNotificationsRead().catch(() => {});
    setUnreadCount(0);
    setNotifications([]);
  };

  return (
    <div className="support-notification-bell">
      <button
        ref={buttonRef}
        type="button"
        className={`support-notification-bell__button${isOpen ? ' support-notification-bell__button--open' : ''}`}
        onClick={toggleOpen}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? popupId : undefined}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="support-notification-bell__badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div ref={popupRef} id={popupId} role="dialog" aria-label="Notifications" tabIndex={-1}
          className="support-notification-bell__dropdown" style={position}>
          <div className="support-notification-bell__header">
            <span className="support-notification-bell__title">Notifications</span>
            <button
              type="button"
              className="support-notification-bell__read-all"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
            >
              Clear all
            </button>
          </div>

          <div className="support-notification-bell__list">
            {loading ? (
              <div className="support-notification-bell__state">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="support-notification-bell__state">No notifications</div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className={`support-notification-bell__item${notification.read_at ? '' : ' support-notification-bell__item--unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <span className={`support-notification-bell__dot${notification.type === 'loop_input_needed' ? ' support-notification-bell__dot--loop' : ''}`} aria-hidden="true" />
                  <span className="support-notification-bell__item-content">
                    {notification.type === 'loop_input_needed' && (
                      <span className="support-notification-bell__action-label">
                        <i className="fa-solid fa-repeat" aria-hidden="true" /> Needs input
                      </span>
                    )}
                    <span className="support-notification-bell__item-title">
                      {notification.title}
                    </span>
                    <span className="support-notification-bell__item-body">
                      {getNotificationPreview(notification)}
                    </span>
                    <span className="support-notification-bell__item-time">
                      {formatTimeAgo(notification.created_at)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>, document.body
      )}
    </div>
  );
};
